"use client";

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

export type PrismDockMode = "closed" | "open-docked" | "minimized";

const STORAGE_KEY = "prism:dock-width";
const SMALL_SCREEN_BREAKPOINT = 900;

const MIN_WIDTH = 360;
/** Fraction of viewport used for the default (comfortable) panel width. */
const DEFAULT_FRACTION = 0.46;
/** Largest fraction the panel may occupy on large screens. */
const MAX_FRACTION = 0.85;
/** Fraction the panel expands to when the user maximizes it. */
const EXPANDED_FRACTION = 0.72;
/** Snap targets (fractions of viewport) applied on resize release. */
const SNAP_FRACTIONS = [0.4, 0.5, 0.66, 0.8];
const SNAP_THRESHOLD_PX = 28;

function viewportWidth(): number {
    if (typeof window === "undefined") return 1440;
    return window.innerWidth || 1440;
}

function clampWidth(width: number, vw = viewportWidth()): number {
    const max = Math.round(vw * MAX_FRACTION);
    const min = Math.min(MIN_WIDTH, max);
    return Math.min(max, Math.max(min, Math.round(width)));
}

function defaultWidth(vw = viewportWidth()): number {
    return clampWidth(Math.round(vw * DEFAULT_FRACTION), vw);
}

function readStoredWidth(): number | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

/** Snap a width to the nearest preset if it's within threshold. */
function snapWidth(width: number, vw = viewportWidth()): number {
    let best = width;
    let bestDist = SNAP_THRESHOLD_PX + 1;
    for (const f of SNAP_FRACTIONS) {
        const target = Math.round(vw * f);
        const dist = Math.abs(target - width);
        if (dist < bestDist) {
            bestDist = dist;
            best = target;
        }
    }
    return bestDist <= SNAP_THRESHOLD_PX ? clampWidth(best, vw) : clampWidth(width, vw);
}

export interface OpenPrismDockOptions {
    seedMessage?: string;
}

interface PrismDockContextValue {
    mode: PrismDockMode;
    drawerWidth: number;
    isDockedOpen: boolean;
    isSmallScreen: boolean;
    isExpanded: boolean;
    openDock: (opts?: OpenPrismDockOptions) => void;
    closeDock: () => void;
    minimizeDock: () => void;
    setDrawerWidth: (width: number) => void;
    commitDrawerWidth: (width: number) => void;
    clampDrawerWidth: (width: number) => number;
    toggleExpanded: () => void;
    consumeSeedMessage: () => string | undefined;
    seedMessage: string | undefined;
    clearSeedMessage: () => void;
}

const PrismDockContext = createContext<PrismDockContextValue | null>(null);

export function PrismDockProvider({ children }: { children: React.ReactNode }) {
    const [mode, setMode] = useState<PrismDockMode>("closed");
    const [drawerWidth, setDrawerWidthState] = useState<number>(() => {
        const stored = readStoredWidth();
        return stored != null ? clampWidth(stored) : defaultWidth();
    });
    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
        () => typeof window !== "undefined" && window.innerWidth < SMALL_SCREEN_BREAKPOINT,
    );
    const [seedMessage, setSeedMessage] = useState<string | undefined>(undefined);
    const seedRef = useRef<string | undefined>(undefined);
    /** Width to restore to when un-maximizing. */
    const restoreWidthRef = useRef<number | null>(null);

    // Keep width clamped and small-screen flag in sync with the viewport.
    // setState only fires inside the resize callback (external-system subscription).
    useEffect(() => {
        const onResize = () => {
            setIsSmallScreen(window.innerWidth < SMALL_SCREEN_BREAKPOINT);
            setDrawerWidthState((w) => clampWidth(w));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const clampDrawerWidth = useCallback((width: number) => clampWidth(width), []);

    const persistWidth = useCallback((width: number) => {
        if (typeof window === "undefined") return;
        try {
            window.localStorage.setItem(STORAGE_KEY, String(width));
        } catch {
            /* ignore quota / private mode */
        }
    }, []);

    // Live update during drag (no persistence, no snap).
    const setDrawerWidth = useCallback((width: number) => {
        setDrawerWidthState(clampWidth(width));
    }, []);

    // Commit on drag release: snap to nearest preset + persist.
    const commitDrawerWidth = useCallback(
        (width: number) => {
            const snapped = snapWidth(width);
            restoreWidthRef.current = null;
            setDrawerWidthState(snapped);
            persistWidth(snapped);
        },
        [persistWidth],
    );

    const toggleExpanded = useCallback(() => {
        setDrawerWidthState((current) => {
            const expandedTarget = clampWidth(Math.round(viewportWidth() * EXPANDED_FRACTION));
            const isExpandedNow = current >= expandedTarget - 4;
            if (isExpandedNow) {
                const restore = restoreWidthRef.current ?? defaultWidth();
                restoreWidthRef.current = null;
                persistWidth(restore);
                return restore;
            }
            restoreWidthRef.current = current;
            persistWidth(expandedTarget);
            return expandedTarget;
        });
    }, [persistWidth]);

    const openDock = useCallback((opts?: OpenPrismDockOptions) => {
        const seed = opts?.seedMessage?.trim();
        if (seed) {
            seedRef.current = seed;
            setSeedMessage(seed);
        }
        setMode("open-docked");
    }, []);

    const clearSeedMessage = useCallback(() => {
        seedRef.current = undefined;
        setSeedMessage(undefined);
    }, []);

    const closeDock = useCallback(() => {
        setMode("closed");
    }, []);

    const minimizeDock = useCallback(() => {
        setMode("minimized");
    }, []);

    const consumeSeedMessage = useCallback(() => {
        const msg = seedRef.current;
        seedRef.current = undefined;
        return msg;
    }, []);

    const isExpanded = useMemo(
        () => drawerWidth >= clampWidth(Math.round(viewportWidth() * EXPANDED_FRACTION)) - 4,
        [drawerWidth],
    );

    const value = useMemo(
        (): PrismDockContextValue => ({
            mode,
            drawerWidth,
            isDockedOpen: mode === "open-docked",
            isSmallScreen,
            isExpanded,
            openDock,
            closeDock,
            minimizeDock,
            setDrawerWidth,
            commitDrawerWidth,
            clampDrawerWidth,
            toggleExpanded,
            consumeSeedMessage,
            seedMessage,
            clearSeedMessage,
        }),
        [
            mode,
            drawerWidth,
            isSmallScreen,
            isExpanded,
            openDock,
            closeDock,
            minimizeDock,
            setDrawerWidth,
            commitDrawerWidth,
            clampDrawerWidth,
            toggleExpanded,
            consumeSeedMessage,
            seedMessage,
            clearSeedMessage,
        ],
    );

    return <PrismDockContext.Provider value={value}>{children}</PrismDockContext.Provider>;
}

export function usePrismDock(): PrismDockContextValue {
    const ctx = useContext(PrismDockContext);
    if (!ctx) {
        throw new Error("usePrismDock must be used within PrismDockProvider");
    }
    return ctx;
}

/** Safe hook for optional dock integration (returns null outside provider). */
export function usePrismDockOptional(): PrismDockContextValue | null {
    return useContext(PrismDockContext);
}
